import { Button } from "../Button";
import { ConfirmationModal } from "../ConfirmationModal";
import { updateDecisionQuestions, getDecisionQuestions } from "../../api/decision/questions";
import { PropTypes } from "prop-types";
import { QueryLoader } from "../Loader";
import { Search } from "../Search";
import { StyledModal } from "../../styles/StyledModal";
import { StyledTableContainer, StyledTableHeader, StyledTableSearch } from "../../styles/StyledUnit/StyledContent";
import { DecisionQuestionItem } from "./DecisionQuestionItem"; 
import { useQuery } from "react-query";
import { useState, useEffect } from "react";
import AddIcon from "../../assets/img/icons/add.svg";
import Modal from "react-modal";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useDecisionAccess from "../../hooks/useDecisionAccess";
import { StyledDecisionTableHeader } from "../../styles/StyledUnit/StyledDecisionTable";

export default function DecisionQuestionsList({ id, newDecision = false }) {
	const { axiosPrivate } = useAxiosPrivate();
	const { canEdit } = useDecisionAccess(newDecision ? null : id);
	// Questions are part of the content: readers only see them
	const editable = newDecision || canEdit;
	const [searchPhrase, setSearchPhrase] = useState(null);
	const [tempQuestions, setTempQuestions] = useState([]);
	
	// For new decisions, don't make API calls - use temporary storage
	const shouldUseAPI = !newDecision && id;
	const { isLoading, isError, error, data, refetch } = shouldUseAPI
		? useQuery(["questionsTab", id, searchPhrase], () => getDecisionQuestions(axiosPrivate, id, searchPhrase))
		: { isLoading: false, isError: false, error: null, data: tempQuestions, refetch: () => {} };
		
	const [addNew, setAddNew] = useState(false);
	const [modalIsOpen, setModalIsOpen] = useState(false);
	const [deletedId, setDeletedId] = useState(null);
	const [questionList, setQuestionList] = useState([]);
	const [editingId, setEditingId] = useState(null);

	// Load temporary questions from sessionStorage for new decisions
	useEffect(() => {
		if (newDecision) {
			const storedQuestions = JSON.parse(sessionStorage.getItem("tempDecisionQuestions") || "[]");
			setTempQuestions(storedQuestions);
			setQuestionList(storedQuestions);
		}
	}, [newDecision]);

	const setPhrase = (phrase) => {
		setSearchPhrase(phrase);
		refetch();
	};
	
	useEffect(() => {
		if (Array.isArray(data)) {
			setQuestionList([...data]);
		}
	}, [data]);

	function handleReorder(fromIndex, toIndex) {
		if (fromIndex === toIndex) return;

		const updated = [...questionList];
		const [moved] = updated.splice(fromIndex, 1);
		updated.splice(toIndex, 0, moved);
		setQuestionList(updated);
		
		if (newDecision) {
			// For new decisions, store in sessionStorage
			sessionStorage.setItem("tempDecisionQuestions", JSON.stringify(updated));
			setTempQuestions(updated);
		} else {
			const payload = updated.map((q) => ({ question: q.question }));
			updateDecisionQuestions(axiosPrivate, refetch, id, payload);
		}
	}

	const handleDelete = async (questionIdToDelete) => {
		try {
			if (newDecision) {
				// For new decisions, handle locally
				const updatedQuestions = tempQuestions.filter((q) => q.id !== questionIdToDelete);
				setTempQuestions(updatedQuestions);
				setQuestionList(updatedQuestions);
				sessionStorage.setItem("tempDecisionQuestions", JSON.stringify(updatedQuestions));
				setDeletedId(null);
				setModalIsOpen(false);
				return;
			}

			const currentQuestions = await getDecisionQuestions(axiosPrivate, id);

			if (!Array.isArray(currentQuestions)) {
				throw new Error("Failed to fetch current questions");
			}
			const updatedQuestions = currentQuestions
			.filter((q) => q.id !== questionIdToDelete)
			.map((q) => ({ question: q.question }));
			await updateDecisionQuestions(axiosPrivate, refetch, id, updatedQuestions);
			await refetch();
			setDeletedId(null);
			setModalIsOpen(false);
		} catch (error) {
			console.error("Failed to delete question:", error);
		}
	};


	if (!isLoading) {
		if (error) console.log(error);
		if (data?.status === 403) return <h6>User not allowed to view questions</h6>;
	}

	return (
		<>
			<StyledTableSearch>
				<Search isWhite setPhrase={setPhrase} />
			</StyledTableSearch>
			{!isLoading && !isError ? (
				<>
					<StyledDecisionTableHeader>
						<span>Order</span>
						<span>Question</span>
						<span>Actions</span>
					</StyledDecisionTableHeader>
					<StyledTableContainer>
						{addNew && <DecisionQuestionItem id={id} refetch={refetch} setAddNew={setAddNew} edit={true} data={{}} newDecision={newDecision} index={questionList.length} total={questionList.length + 1} />}
						{questionList.map((e, i) => (
							<DecisionQuestionItem
								id={id}
								refetch={refetch}
								key={e.id}
								data={e}
								index={i}
								total={questionList.length}
								onReorder={(from, to) => handleReorder(from, to)}
								openModal={() => setModalIsOpen(true)}
								setDeletedId={setDeletedId}
								editingId={editingId}
								setEditingId={setEditingId}
								newDecision={newDecision}
								readOnly={!editable}
							/>
						))}
						{data.length < 1 && <h6>No questions added yet</h6>}
						{editable && (
							<Button icon={AddIcon} onClick={() => setAddNew(true)}>
								Add question
							</Button>
						)}
					</StyledTableContainer>
				</>
			) : (
				<QueryLoader />
			)}
			<Modal
				isOpen={modalIsOpen}
				onRequestClose={() => {
					setDeletedId(null);
					setModalIsOpen(false);
				}}
				style={StyledModal}
				appElement={document.getElementById("app")}
			>
				<ConfirmationModal
					handleDelete={handleDelete}
					deletedId={deletedId}
					closeModal={() => {
						setDeletedId(null);
						setModalIsOpen(false);
					}}
				/>
			</Modal>
		</>
	);
}

DecisionQuestionsList.propTypes = {
	id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	newDecision: PropTypes.bool,
};
